// src/components/Dispositivos/CrearDispositivo.jsx
import React, { useEffect, useState } from "react";
import api from "../../services/Api"; // 👈 respeta la A mayúscula
import { getApiError } from "../../services/apiError";

const formatearMac = (valor) => {
  const hexadecimal = valor.toUpperCase().replace(/[^0-9A-F]/g, "").slice(0, 12);
  return hexadecimal.match(/.{1,2}/g)?.join(":") || "";
};

const CrearDispositivo = ({ initialEstanqueId, onCreated }) => {
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState(""); // MAC
  const [estanqueId, setEstanqueId] = useState(String(initialEstanqueId || new URLSearchParams(window.location.search).get("estanqueId") || ""));
  const [guardando, setGuardando] = useState(false);
  const [estanques, setEstanques] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [claveCreada, setClaveCreada] = useState(null);

  // 🔐 Seguridad del dispositivo
  const [requierePassword, setRequierePassword] = useState(false);
  const [passwordPlano, setPasswordPlano] = useState("");
  const [mostrarPass, setMostrarPass] = useState(false);

  useEffect(() => {
    const obtenerEstanques = async () => {
      try {
        const { data } = await api.get("/Estanques/mis-estanques");
        setEstanques(data || []);
      } catch (error) {
        console.error("Error al obtener estanques", error);
        setMensaje("❌ Error al cargar los estanques");
      }
    };
    obtenerEstanques();
  }, []);

  const handleCrear = async (e) => {
    e.preventDefault();
    if (guardando) return;
    setMensaje("");

    // Validación mínima cuando se exige contraseña
    if (requierePassword && (!passwordPlano || passwordPlano.length < 4)) {
      setMensaje("❌ La contraseña debe tener al menos 4 caracteres.");
      return;
    }

    const macOk = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(codigo);
    if (!macOk) {
      setMensaje("❌ Completa la MAC con el formato AA:BB:CC:DD:EE:FF.");
      return;
    }

    try {
      setGuardando(true);
      const { data } = await api.post("/Dispositivos/crear", {
        nombre,
        codigo: codigo.replace(/:/g, ""),
        estanqueId: parseInt(estanqueId, 10),
        requierePassword,
        passwordPlano: requierePassword ? passwordPlano : null,
      });
      setClaveCreada(data);
      onCreated?.({ id: data.id, nombre, estanqueId: Number(estanqueId) });

      setMensaje("✅ Dispositivo creado correctamente");
      setNombre("");
      setCodigo("");
      setEstanqueId(String(initialEstanqueId || new URLSearchParams(window.location.search).get("estanqueId") || ""));

      // reset de seguridad
      setRequierePassword(false);
      setPasswordPlano("");
      setMostrarPass(false);
    } catch (error) {
      console.error("Error al crear dispositivo", error);
      setMensaje(`❌ ${getApiError(error, "Error al crear el dispositivo")}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-neutral-900 text-white p-4 sm:p-6 rounded-lg max-w-lg sm:max-w-xl md:max-w-2xl mx-auto w-full">
      <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-green-400">
        Registrar Nuevo Dispositivo (ESP32)
      </h3>

      {mensaje && (
        <div
          className={`mb-4 text-sm sm:text-base ${
            mensaje.startsWith("✅") ? "text-green-400" : "text-red-400"
          }`}
        >
          {mensaje}
        </div>
      )}

      <form onSubmit={handleCrear} className="space-y-4">
        {/* Datos básicos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1">
            <label className="block mb-1 text-sm sm:text-base">
              Nombre del Dispositivo:
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-gray-800 border border-gray-600 text-sm sm:text-base"
              required
            />
          </div>

          <div className="col-span-1">
            <label className="block mb-1 text-sm sm:text-base">
              Código Único del Dispositivo (MAC):
            </label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(formatearMac(e.target.value))}
              placeholder="AA:BB:CC:DD:EE:FF"
              maxLength={17}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              pattern="([0-9A-F]{2}:){5}[0-9A-F]{2}"
              title="Escribe los 12 caracteres de la MAC; los dos puntos se agregan automáticamente."
              className="w-full p-2 sm:p-3 rounded bg-gray-800 border border-gray-600 text-sm sm:text-base uppercase font-mono tracking-wide"
              required
            />
          </div>
        </div>

        {/* Estanque */}
        <div>
          <label className="block mb-1 text-sm sm:text-base">
            Estanque al que pertenece:
          </label>
          <select
            value={estanqueId}
            onChange={(e) => setEstanqueId(e.target.value)}
            className="w-full p-2 sm:p-3 rounded bg-gray-800 border border-gray-600 text-sm sm:text-base"
            required
          >
            <option value="">-- Selecciona un estanque --</option>
            {estanques.map((estanque) => (
              <option key={estanque.id} value={estanque.id}>
                {estanque.nombre}
              </option>
            ))}
          </select>
        </div>

        {/* 🔐 Seguridad del dispositivo */}
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requierePassword}
              onChange={(e) => setRequierePassword(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm sm:text-base">
              Requerir contraseña para configurar este dispositivo
            </span>
          </label>

          {requierePassword && (
            <div>
              <label className="block mb-1 text-sm sm:text-base">
                Contraseña del dispositivo:
              </label>
              <div className="flex">
                <input
                  type={mostrarPass ? "text" : "password"}
                  value={passwordPlano}
                  onChange={(e) => setPasswordPlano(e.target.value)}
                  className="flex-1 p-2 sm:p-3 rounded-l bg-gray-800 border border-gray-600 text-sm sm:text-base"
                  placeholder="Mínimo 4 caracteres"
                  minLength={4}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarPass((v) => !v)}
                  className="px-3 sm:px-4 bg-gray-700 border border-l-0 border-gray-600 rounded-r text-sm"
                  title={mostrarPass ? "Ocultar" : "Mostrar"}
                >
                  {mostrarPass ? "🙈" : "👁️"}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                La contraseña se protege con BCrypt en el backend. Puedes
                cambiarla luego desde “Editar dispositivo”.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={guardando}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded text-sm sm:text-base"
          >
            Crear Dispositivo
          </button>
        </div>
      </form>
      {claveCreada && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"><div className="max-w-xl w-full bg-neutral-900 border border-amber-500 rounded-xl p-5 space-y-4"><h4 className="text-xl font-bold text-amber-300">Clave IoT creada</h4><p>Esta clave sólo se muestra ahora. Guárdala y configura el firmware para enviar <code>{claveCreada.header}: &lt;clave&gt;</code>.</p><textarea readOnly value={claveCreada.apiKey} rows={3} className="w-full bg-black border border-neutral-600 rounded p-3 font-mono"/><div className="flex gap-2"><button type="button" onClick={() => navigator.clipboard?.writeText(claveCreada.apiKey)} className="bg-cyan-700 px-4 py-2 rounded">Copiar</button><button type="button" onClick={() => setClaveCreada(null)} className="bg-amber-600 text-black px-4 py-2 rounded">Confirmo que la guardé</button></div></div></div>}
    </div>
  );
};

export default CrearDispositivo;
